// @ts-nocheck
import { supabase } from "@/integrations/supabase/client";
import { isClientCourtCost } from '@/utils/courtCosts';

export interface ProcessFinancialData {
  id: string;
  title: string;
  client_fee: string;
  court_costs: string;
  success_fee?: string;
  success_fee_type?: 'percentage' | 'fixed' | 'conditional';
  success_fee_conditions?: string;
  success_fee_trigger?: 'sentenca' | 'acordo' | 'liminar' | 'outro';
  payment_method: 'a_vista' | 'parcelado' | 'condicional' | 'por_fase';
  installment_count: number;
  installment_frequency: 'mensal' | 'bimestral' | 'trimestral' | 'semestral' | 'anual';
  first_payment_date?: Date;
  late_payment_interest: string;
  early_payment_discount: string;
  payment_conditions: string;
  collection_notes: string;
  phase_fees: Array<{
    phase: string;
    amount: string;
    due_date?: Date;
    description: string;
  }>;
  client_id?: string;
  filing_date?: Date;
  user_id: string;
  administrative_process_id?: string;
  // Nova configuração avançada de cobranças
  fee_configuration?: {
    hasInitialFee: boolean;
    initialFeeAmount: number;
    initialFeeType: 'fixed' | 'percentage';
    hasSuccessFee: boolean;
    successFeeType: 'percentage_global' | 'percentage_installment' | 'fixed_global' | 'fixed_monthly';
    successFeeValue: number;
    successFeeTrigger: 'decisao' | 'deferimento' | 'indeferimento' | 'recurso_procedente' | 'acordo' | 'outro';
    successFeeConditions: string;
    paymentMethod: 'a_vista' | 'parcelado' | 'entrada_mais_parcelas';
    hasDownPayment: boolean;
    downPaymentAmount: number;
    downPaymentType: 'fixed' | 'percentage';
    installmentCount: number;
    installmentFrequency: 'mensal' | 'bimestral' | 'trimestral' | 'semestral';
    latePaymentInterest: number;
    earlyPaymentDiscount: number;
    gracePeriodDays: number;
    paymentConditions: string;
    collectionNotes: string;
  };
}

export interface FinancialEntry {
  description: string;
  amount: number;
  entry_type: 'receita' | 'despesa';
  category: string;
  payment_date?: string;
  due_date?: string;
  status: string;
  client_id?: string;
  case_id?: string | null;
  administrative_process_id?: string | null;
  is_installment?: boolean;
  installments_count?: number;
  installment_frequency?: string;
  payment_notes?: string;
  user_id: string;
}

export class FinancialIntegrationService {
  /**
   * Cria automaticamente entradas financeiras baseadas nos dados do processo
   */
  static async createFinancialEntriesFromProcess(processData: ProcessFinancialData): Promise<void> {
    try {
      const entries: FinancialEntry[] = [];
      const config = processData.fee_configuration;

      // 1. Criar entrada para Honorários Iniciais (se configurado)
      if (config?.hasInitialFee && config.initialFeeAmount > 0) {
        await this.createInitialFeeEntries(processData, config, entries);
      } else if (!config && processData.client_fee && parseFloat(processData.client_fee) > 0) {
        // Fallback para configuração legada
        await this.createLegacyFeeEntries(processData, entries);
      }

      // 2. Criar entrada para Honorários de Êxito (se configurado)
      if (config?.hasSuccessFee && config.successFeeValue > 0) {
        await this.createSuccessFeeEntries(processData, config, entries);
      }

      // 3. Criar entrada para Custas Processuais (Despesa do Cliente - apenas informativa)
      if (processData.court_costs && parseFloat(processData.court_costs) > 0) {
        const entry: any = {
          description: `Custas Processuais - ${processData.title}`,
          amount: parseFloat(processData.court_costs),
          entry_type: 'despesa',
          category: 'Custas Processuais (Cliente)',
          due_date: processData.filing_date?.toISOString() || new Date().toISOString(),
          status: 'Pendente',
          payment_notes: 'Custas processuais - responsabilidade do cliente. Entrada apenas informativa.',
          user_id: processData.user_id
        };
        
        // Use administrative_process_id for admin processes, case_id for judicial cases
        if (processData.administrative_process_id) {
          entry.administrative_process_id = processData.administrative_process_id;
        } else {
          entry.case_id = processData.id;
        }
        
        entries.push(entry);
      }

      // 3. Criar entrada para Honorários de Êxito (Receita Condicional)
      // NÃO criar entradas de honorários de êxito aqui - isso deve ser feito apenas pelo SuccessFeeActivationService
      // quando a data do primeiro recebimento for definida
      if (processData.success_fee && processData.success_fee_type !== 'conditional' && !processData.administrative_process_id) {
        // Apenas para processos judiciais, não administrativos
        let amount = 0;
        if (processData.success_fee_type === 'percentage') {
          // Para processos administrativos, usar o valor dos honorários como base se não houver valor da causa
          const baseValue = parseFloat(processData.client_fee) || 1000; // valor mínimo de referência
          amount = (parseFloat(processData.success_fee) / 100) * baseValue;
        } else if (processData.success_fee_type === 'fixed') {
          amount = parseFloat(processData.success_fee);
        }

        if (amount > 0) {
          const entry: any = {
            description: `Honorários de Êxito - ${processData.title}`,
            amount: amount,
            entry_type: 'receita',
            category: 'Honorários de Êxito',
            status: 'Condicional',
            client_id: processData.client_id,
            payment_notes: `Gatilho: ${processData.success_fee_trigger}\nCondições: ${processData.success_fee_conditions || 'Não especificadas'}`,
            user_id: processData.user_id
          };
          
          // Use administrative_process_id for admin processes, case_id for judicial cases
          if (processData.administrative_process_id) {
            entry.administrative_process_id = processData.administrative_process_id;
          } else {
            entry.case_id = processData.id;
          }
          
          entries.push(entry);
        }
      }

      // 4. Inserir todas as entradas no banco
      if (entries.length > 0) {
        console.log('Entradas a serem inseridas:', entries.map(e => ({ 
          description: e.description, 
          case_id: e.case_id, 
          administrative_process_id: e.administrative_process_id 
        })));
        
        // Remover due_date das entradas antes de inserir no banco (campo ainda não existe)
        const entriesForDB = entries.map(entry => {
          const { due_date, ...entryWithoutDueDate } = entry as any;
          return entryWithoutDueDate;
        });

        const { error } = await supabase
          .from('financial_entries')
          .insert(entriesForDB);

        if (error) {
          console.error('Erro ao criar entradas financeiras:', error);
          throw new Error('Falha ao integrar dados financeiros');
        }

        console.log(`${entries.length} entradas financeiras criadas automaticamente`);
      }
    } catch (error) {
      console.error('Erro na integração financeira:', error);
      throw error;
    }
  }

  /**
   * Atualiza entradas financeiras existentes quando um processo é editado
   * Agora verifica se realmente há mudanças antes de remover e recriar
   */
  static async updateFinancialEntriesFromProcess(processData: ProcessFinancialData): Promise<void> {
    try {
      // Buscar entradas existentes para comparação
      let existingEntries: any[] = [];
      if (processData.administrative_process_id) {
        const { data } = await supabase
          .from('financial_entries')
          .select('*')
          .eq('administrative_process_id', processData.administrative_process_id);
        existingEntries = data || [];
      } else {
        const { data } = await supabase
          .from('financial_entries')
          .select('*')
          .eq('case_id', processData.id);
        existingEntries = data || [];
      }

      // Se não há entradas existentes, criar novas
      if (existingEntries.length === 0) {
        await this.createFinancialEntriesFromProcess(processData);
        return;
      }

      // Verificar se há mudanças significativas nos dados financeiros
      const hasFinancialChanges = this.hasSignificantFinancialChanges(existingEntries, processData);
      
      if (hasFinancialChanges) {
        console.log('Mudanças financeiras detectadas, atualizando entradas...');
        // Remover entradas existentes
        if (processData.administrative_process_id) {
          await this.removeFinancialEntriesForAdministrativeProcess(processData.administrative_process_id);
        } else {
          await this.removeFinancialEntriesForProcess(processData.id);
        }
        // Criar novas entradas com dados atualizados
        await this.createFinancialEntriesFromProcess(processData);
      } else {
        console.log('Nenhuma mudança financeira significativa detectada, mantendo entradas existentes.');
      }
    } catch (error) {
      console.error('Erro ao atualizar entradas financeiras:', error);
      throw error;
    }
  }

  /**
   * Remove entradas financeiras quando um processo é excluído
   */
  static async removeFinancialEntriesForProcess(caseId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('financial_entries')
        .delete()
        .eq('case_id', caseId);

      if (error) {
        console.error('Erro ao remover entradas financeiras:', error);
        throw new Error('Falha ao remover dados financeiros');
      }
    } catch (error) {
      console.error('Erro ao remover entradas financeiras:', error);
      throw error;
    }
  }

  /**
   * Remove entradas financeiras quando um processo administrativo é excluído
   */
  static async removeFinancialEntriesForAdministrativeProcess(adminProcessId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('financial_entries')
        .delete()
        .eq('administrative_process_id', adminProcessId);

      if (error) {
        console.error('Erro ao remover entradas financeiras:', error);
        throw new Error('Falha ao remover dados financeiros');
      }
    } catch (error) {
      console.error('Erro ao remover entradas financeiras:', error);
      throw error;
    }
  }

  /**
   * Calcula a data de uma parcela baseada na frequência
   */
  /**
   * Cria entradas para honorários iniciais com configuração avançada
   */
  private static async createInitialFeeEntries(
    processData: ProcessFinancialData, 
    config: NonNullable<ProcessFinancialData['fee_configuration']>, 
    entries: FinancialEntry[]
  ): Promise<void> {
    const amount = config.initialFeeAmount;
    const startDate = processData.first_payment_date || new Date();

    if (config.paymentMethod === 'a_vista') {
      // Pagamento à vista
      const entry: any = {
          description: `Honorários Iniciais - ${processData.title}`,
          amount: amount,
          entry_type: 'receita',
          category: 'Honorários Iniciais',
          due_date: startDate.toISOString(),
          status: 'Pendente',
          client_id: processData.client_id,
          payment_notes: this.generateAdvancedPaymentNotes(processData, config),
          user_id: processData.user_id
        };
        
        // Use administrative_process_id for admin processes, case_id for judicial cases
        if ((processData as any).administrative_process_id) {
          entry.administrative_process_id = (processData as any).administrative_process_id;
        } else {
          entry.case_id = processData.id;
        }
        
        entries.push(entry);
    } else if (config.paymentMethod === 'entrada_mais_parcelas' && config.hasDownPayment) {
      // Entrada + parcelas
      const downPayment = config.downPaymentType === 'percentage' 
        ? (amount * config.downPaymentAmount / 100)
        : config.downPaymentAmount;
      const remainingAmount = amount - downPayment;
      const installmentAmount = remainingAmount / config.installmentCount;

      // Entrada
      const downPaymentEntry: any = {
        description: `Honorários Iniciais (Entrada) - ${processData.title}`,
        amount: downPayment,
        entry_type: 'receita',
        category: 'Honorários Iniciais',
        due_date: startDate.toISOString(),
        status: 'Pendente',
        client_id: processData.client_id,
        payment_notes: this.generateAdvancedPaymentNotes(processData, config),
        user_id: processData.user_id
      };
      
      // Use administrative_process_id for admin processes, case_id for judicial cases
      if (processData.administrative_process_id) {
        downPaymentEntry.administrative_process_id = processData.administrative_process_id;
      } else {
        downPaymentEntry.case_id = processData.id;
      }
      
      entries.push(downPaymentEntry);

        // Parcelas
        for (let i = 0; i < config.installmentCount; i++) {
          const installmentDate = this.calculateInstallmentDate(startDate, i + 1, config.installmentFrequency);
          const installmentEntry: any = {
            description: `Honorários Iniciais - ${processData.title} (${i + 1}/${config.installmentCount})`,
            amount: installmentAmount,
            entry_type: 'receita',
            category: 'Honorários Iniciais',
            due_date: installmentDate.toISOString(),
            status: 'Pendente',
            client_id: processData.client_id,
            is_installment: true,
            installments_count: config.installmentCount,
            installment_frequency: config.installmentFrequency,
            payment_notes: this.generateAdvancedPaymentNotes(processData, config),
            user_id: processData.user_id
          };
          
          // Use administrative_process_id for admin processes, case_id for judicial cases
          if (processData.administrative_process_id) {
            installmentEntry.administrative_process_id = processData.administrative_process_id;
          } else {
            installmentEntry.case_id = processData.id;
          }
          
          entries.push(installmentEntry);
        }
    } else if (config.paymentMethod === 'parcelado') {
      // Apenas parcelas
      const installmentAmount = amount / config.installmentCount;
      
      for (let i = 0; i < config.installmentCount; i++) {
        const installmentDate = this.calculateInstallmentDate(startDate, i + 1, config.installmentFrequency);
        const installmentEntry: any = {
          description: `Honorários Iniciais - ${processData.title} (${i + 1}/${config.installmentCount})`,
          amount: installmentAmount,
          entry_type: 'receita',
          category: 'Honorários Iniciais',
          due_date: installmentDate.toISOString(),
          status: 'Pendente',
          client_id: processData.client_id,
          is_installment: true,
          installments_count: config.installmentCount,
          installment_frequency: config.installmentFrequency,
          payment_notes: this.generateAdvancedPaymentNotes(processData, config),
          user_id: processData.user_id
        };
        
        // Use administrative_process_id for admin processes, case_id for judicial cases
        if (processData.administrative_process_id) {
          installmentEntry.administrative_process_id = processData.administrative_process_id;
        } else {
          installmentEntry.case_id = processData.id;
        }
        
        entries.push(installmentEntry);
      }
    }
  }

  /**
   * Cria entradas para honorários de êxito com configuração avançada
   * NÃO criar entradas para processos administrativos - isso deve ser feito apenas pelo SuccessFeeActivationService
   */
  private static async createSuccessFeeEntries(
    processData: ProcessFinancialData, 
    config: NonNullable<ProcessFinancialData['fee_configuration']>, 
    entries: FinancialEntry[]
  ): Promise<void> {
    // NÃO criar entradas de honorários de êxito para processos administrativos
    // Isso deve ser feito apenas pelo SuccessFeeActivationService quando a data for definida
    if (processData.administrative_process_id) {
      console.log('Pulando criação de honorários de êxito para processo administrativo - será criado pelo SuccessFeeActivationService');
      return;
    }

    const description = `Honorários de Êxito - ${processData.title}`;
    const category = 'Honorários de Êxito';
    const paymentNotes = `${config.successFeeConditions}\n${this.generateAdvancedPaymentNotes(processData, config)}`;

    if (config.successFeeType === 'fixed_global') {
      // Valor fixo global
      const entry: any = {
        description: description,
        amount: config.successFeeValue,
        entry_type: 'receita',
        category: category,
        payment_date: new Date().toISOString(), // Será atualizado quando o gatilho for acionado
        status: 'Condicional', // Status especial para honorários de êxito
        client_id: processData.client_id,
        payment_notes: paymentNotes,
        user_id: processData.user_id
      };
      
      // Use administrative_process_id for admin processes, case_id for judicial cases
      if (processData.administrative_process_id) {
        entry.administrative_process_id = processData.administrative_process_id;
      } else {
        entry.case_id = processData.id;
      }
      
      entries.push(entry);
    } else if (config.successFeeType === 'fixed_monthly') {
      // Valor fixo mensal - será criado quando o benefício for concedido
      const entry: any = {
        description: `${description} (Mensal)`,
        amount: config.successFeeValue,
        entry_type: 'receita',
        category: category,
        payment_date: new Date().toISOString(),
        status: 'Condicional',
        client_id: processData.client_id,
        payment_notes: `${paymentNotes}\nValor mensal: R$ ${config.successFeeValue.toFixed(2)}`,
        user_id: processData.user_id
      };
      
      // Use administrative_process_id for admin processes, case_id for judicial cases
      if (processData.administrative_process_id) {
        entry.administrative_process_id = processData.administrative_process_id;
      } else {
        entry.case_id = processData.id;
      }
      
      entries.push(entry);
    } else {
      // Porcentagem - será calculada quando o valor do benefício for conhecido
      const entry: any = {
        description: `${description} (${config.successFeeValue}%)`,
        amount: 0, // Será calculado posteriormente
        entry_type: 'receita',
        category: category,
        payment_date: new Date().toISOString(),
        status: 'Condicional',
        client_id: processData.client_id,
        payment_notes: `${paymentNotes}\nPercentual: ${config.successFeeValue}% ${config.successFeeType === 'percentage_global' ? 'do valor global' : 'de cada parcela'}`,
        user_id: processData.user_id
      };
      
      // Use administrative_process_id for admin processes, case_id for judicial cases
      if (processData.administrative_process_id) {
        entry.administrative_process_id = processData.administrative_process_id;
      } else {
        entry.case_id = processData.id;
      }
      
      entries.push(entry);
    }
  }

  /**
   * Cria entradas com configuração legada (fallback)
   */
  private static async createLegacyFeeEntries(
    processData: ProcessFinancialData, 
    entries: FinancialEntry[]
  ): Promise<void> {
    if (processData.payment_method === 'parcelado') {
      const installmentAmount = parseFloat(processData.client_fee) / processData.installment_count;
      const startDate = processData.first_payment_date || new Date();
      
      for (let i = 0; i < processData.installment_count; i++) {
        const installmentDate = this.calculateInstallmentDate(startDate, i, processData.installment_frequency);
        
        const installmentEntry: any = {
          description: `Honorários - ${processData.title} (${i + 1}/${processData.installment_count})`,
          amount: installmentAmount,
          entry_type: 'receita',
          category: 'Honorários',
          payment_date: installmentDate.toISOString(),
          status: 'Pendente',
          client_id: processData.client_id,
          is_installment: true,
          installments_count: processData.installment_count,
          installment_frequency: processData.installment_frequency,
          payment_notes: this.generatePaymentNotes(processData),
          user_id: processData.user_id
        };
        
        // Use administrative_process_id for admin processes, case_id for judicial cases
        if (processData.administrative_process_id) {
          installmentEntry.administrative_process_id = processData.administrative_process_id;
        } else {
          installmentEntry.case_id = processData.id;
        }
        
        entries.push(installmentEntry);
      }
    } else {
      const entry: any = {
        description: `Honorários - ${processData.title}`,
        amount: parseFloat(processData.client_fee),
        entry_type: 'receita',
        category: 'Honorários',
        payment_date: processData.filing_date?.toISOString() || new Date().toISOString(),
        status: 'Pendente',
        client_id: processData.client_id,
        payment_notes: this.generatePaymentNotes(processData),
        user_id: processData.user_id
      };
      
      // Use administrative_process_id for admin processes, case_id for judicial cases
      if (processData.administrative_process_id) {
        entry.administrative_process_id = processData.administrative_process_id;
      } else {
        entry.case_id = processData.id;
      }
      
      entries.push(entry);
    }
  }

  /**
   * Gera notas de pagamento com configuração avançada
   */
  private static generateAdvancedPaymentNotes(
    processData: ProcessFinancialData, 
    config: NonNullable<ProcessFinancialData['fee_configuration']>
  ): string {
    const notes = [];
    
    if (config.paymentConditions) {
      notes.push(`Condições: ${config.paymentConditions}`);
    }
    
    if (config.latePaymentInterest > 0) {
      notes.push(`Juros por atraso: ${config.latePaymentInterest}% ao mês`);
    }
    
    if (config.earlyPaymentDiscount > 0) {
      notes.push(`Desconto para pagamento antecipado: ${config.earlyPaymentDiscount}%`);
    }
    
    if (config.gracePeriodDays > 0) {
      notes.push(`Carência: ${config.gracePeriodDays} dias`);
    }
    
    if (config.collectionNotes) {
      notes.push(`Observações: ${config.collectionNotes}`);
    }
    
    return notes.join('\n');
  }

  private static calculateInstallmentDate(startDate: Date, installmentIndex: number, frequency: string): Date {
    const date = new Date(startDate);
    
    switch (frequency) {
      case 'semanal':
        date.setDate(date.getDate() + (installmentIndex * 7));
        break;
      case 'quinzenal':
        date.setDate(date.getDate() + (installmentIndex * 15));
        break;
      case 'mensal':
        date.setMonth(date.getMonth() + installmentIndex);
        break;
      case 'bimestral':
        date.setMonth(date.getMonth() + (installmentIndex * 2));
        break;
      case 'trimestral':
        date.setMonth(date.getMonth() + (installmentIndex * 3));
        break;
      case 'semestral':
        date.setMonth(date.getMonth() + (installmentIndex * 6));
        break;
      case 'anual':
        date.setFullYear(date.getFullYear() + installmentIndex);
        break;
      default:
        date.setMonth(date.getMonth() + installmentIndex);
    }
    
    return date;
  }

  /**
   * Converte valores para string de forma segura
   */
  private static safeStringConvert(value: any): string {
    if (value === null || value === undefined || value === '') return '';
    return String(value);
  }

  /**
   * Gera notas de pagamento baseadas nos dados do processo
   */
  private static generatePaymentNotes(processData: ProcessFinancialData): string {
    let notes = '';
    
    if (processData.payment_conditions) {
      notes += `Condições: ${processData.payment_conditions}\n`;
    }
    
    if (processData.late_payment_interest) {
      notes += `Juros por atraso: ${processData.late_payment_interest}\n`;
    }
    
    if (processData.early_payment_discount) {
      notes += `Desconto antecipado: ${processData.early_payment_discount}\n`;
    }
    
    if (processData.collection_notes) {
      notes += `Notas de cobrança: ${processData.collection_notes}\n`;
    }
    
    return notes.trim();
  }

  /**
   * Busca todas as entradas financeiras relacionadas a um processo
   */
  static async getFinancialEntriesForProcess(caseId: string): Promise<FinancialEntry[]> {
    try {
      const { data, error } = await supabase
        .from('financial_entries')
        .select('*')
        .eq('case_id', caseId)
        .order('payment_date', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar entradas financeiras:', error);
      return [];
    }
  }

  /**
   * Calcula o resumo financeiro de um processo
   */
  static async getProcessFinancialSummary(caseId: string): Promise<{
    totalRevenue: number;
    totalExpenses: number;
    netProfit: number;
    pendingAmount: number;
    paidAmount: number;
    overdueAmount: number;
  }> {
    try {
      const entries = await this.getFinancialEntriesForProcess(caseId);
      
      let totalRevenue = 0;
      let totalExpenses = 0;
      let pendingAmount = 0;
      let paidAmount = 0;
      let overdueAmount = 0;

      entries.forEach(entry => {
        // Custas pagas pelo cliente não são receita nem despesa do escritório
        if (isClientCourtCost(entry)) return;
        if (entry.entry_type === 'receita') {
          totalRevenue += entry.amount;
          if (entry.status === 'Pago') {
            paidAmount += entry.amount;
          } else if (entry.status === 'Pendente') {
            pendingAmount += entry.amount;
          } else if (entry.status === 'Vencido') {
            overdueAmount += entry.amount;
          }
        } else {
          totalExpenses += entry.amount;
        }
      });

      return {
        totalRevenue,
        totalExpenses,
        netProfit: totalRevenue - totalExpenses,
        pendingAmount,
        paidAmount,
        overdueAmount
      };
    } catch (error) {
      console.error('Erro ao calcular resumo financeiro:', error);
      return {
        totalRevenue: 0,
        totalExpenses: 0,
        netProfit: 0,
        pendingAmount: 0,
        paidAmount: 0,
        overdueAmount: 0
      };
    }
  }

  /**
   * Verifica se há mudanças significativas nos dados financeiros
   */
  private static hasSignificantFinancialChanges(existingEntries: any[], processData: ProcessFinancialData): boolean {
    // Calcular valores esperados baseados nos novos dados
    const expectedClientFee = parseFloat(processData.client_fee || '0');
    const expectedCourtCosts = parseFloat(processData.court_costs || '0');
    const expectedSuccessFee = parseFloat(processData.success_fee || '0');
    
    // Verificar se os valores nas entradas existentes correspondem aos novos valores
    const clientFeeEntries = existingEntries.filter(e => 
      (e.category === 'Honorários' || e.category === 'Honorários Iniciais') && 
      e.administrative_process_id === processData.id
    );
    const courtCostEntries = existingEntries.filter(e => 
      e.category === 'Custas Processuais' && 
      e.administrative_process_id === processData.id
    );
    const successFeeEntries = existingEntries.filter(e => 
      e.category === 'Honorários de Êxito' && 
      e.administrative_process_id === processData.id
    );
    
    // Somar valores existentes
    const existingClientFeeTotal = clientFeeEntries.reduce((sum, e) => sum + e.amount, 0);
    const existingCourtCostTotal = courtCostEntries.reduce((sum, e) => sum + e.amount, 0);
    const existingSuccessFeeTotal = successFeeEntries.reduce((sum, e) => sum + e.amount, 0);
    
    // Verificar se há diferenças significativas (tolerância de R$ 0.01)
    const tolerance = 0.01;
    const clientFeeChanged = Math.abs(existingClientFeeTotal - expectedClientFee) > tolerance;
    const courtCostChanged = Math.abs(existingCourtCostTotal - expectedCourtCosts) > tolerance;
    
    // Para honorários de êxito, verificar apenas se há mudanças estruturais significativas
    // (não comparar valores totais pois dependem do cronograma de benefícios)
    const successFeeStructureChanged = processData.success_fee_enabled && 
      (successFeeEntries.length === 0 || 
       (!processData.success_fee_enabled && successFeeEntries.length > 0));
    
    console.log('Verificação de mudanças financeiras:', {
      clientFeeChanged,
      courtCostChanged,
      successFeeStructureChanged,
      existingClientFeeTotal,
      expectedClientFee,
      existingCourtCostTotal,
      expectedCourtCosts,
      successFeeEntriesCount: successFeeEntries.length,
      successFeeEnabled: processData.success_fee_enabled
    });
    
    return clientFeeChanged || courtCostChanged || successFeeStructureChanged;
  }
}
